
import React, { useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import DistributionCenterPricesHeader from '@/components/distribution-center/DistributionCenterPricesHeader';
import DistributionCenterPricesFilter from '@/components/distribution-center/DistributionCenterPricesFilter';
import DistributionCenterPricesTable from '@/components/distribution-center/DistributionCenterPricesTable';
import DistributionCenterPriceForm from '@/components/distribution-center/DistributionCenterPriceForm';
import DistributionCenterUpload from '@/components/distribution-center/upload/DistributionCenterUpload';
import { useDistributionCenterPrices, useDeleteDistributionCenterPrice } from '@/hooks/use-distribution-center-prices';
import { DistributionCenterPrice, DistributionCenterPriceFilters } from '@/types/database/distribution-center-prices';

const DistributionCenter: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingPrice, setEditingPrice] = useState<DistributionCenterPrice | undefined>();
  const [filters, setFilters] = useState<DistributionCenterPriceFilters>({});

  const { data: prices, isLoading } = useDistributionCenterPrices(filters);
  const deleteMutation = useDeleteDistributionCenterPrice();

  const handleAddNew = () => {
    setEditingPrice(undefined);
    setShowForm(true);
  };

  const handleEdit = (price: DistributionCenterPrice) => {
    setEditingPrice(price);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this price record?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingPrice(undefined);
  };

  const handleUpload = () => {
    setShowUpload(true);
  };

  const handleCloseUpload = () => {
    setShowUpload(false);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <LayoutGrid className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Distribution Center Prices</h1>
          </div>
          <p className="text-gray-600">
            Monitor distribution center pricing data and track price trends across different locations
          </p>
        </div>

        <DistributionCenterPricesHeader 
          onAddNew={handleAddNew}
          onUpload={handleUpload}
        />

        <div className="space-y-6">
          <DistributionCenterPricesFilter
            filters={filters}
            onFiltersChange={setFilters}
            onClearFilters={handleClearFilters}
          />

          <DistributionCenterPricesTable
            prices={prices || []}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={isLoading}
          />
        </div>

        <DistributionCenterPriceForm
          open={showForm}
          onClose={handleCloseForm}
          price={editingPrice}
        />

        <DistributionCenterUpload
          open={showUpload}
          onClose={handleCloseUpload}
        />
      </div>
    </Layout>
  );
};

export default DistributionCenter;
